import { useMemo } from "react";
import { Box, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { RagAssistantMessage, RagImageItem } from "../../types/ragChatTypes";
import { badgeForQueryType } from "../../constants/ragUiConstants";
import {
  buildDisplayPageAlerts,
  mergePageAlerts,
  sanitizePageAlertsForSafetyRail,
  splitAnswerContent,
} from "../../utils/answerContentUtils";
import {
  alternativePdfsNotInSources,
  extractSafetyPrecautionsAlerts,
  getUnreferencedVideos,
  isProcedureAnswer,
  isVideoOwnedProcedureAnswer,
  selectMainAndMoreDiagramImages,
} from "../../utils/ragChatUtils";
import { evaluatePartsCatalogPresentation } from "../../utils/partsCatalogUtils";
import { RagAnswerText } from "./RagAnswerText";
import {
  RagAlertsSection,
  RagAlternativePdfs,
  RagAlternativeSources,
  RagBestAnswerHeader,
  RagOptionButtons,
  RagImageRail,
  RagRelatedQuestions,
  RagSourcesList,
  RagTablesSection,
  RagVideosList,
} from "./RagChatBlocks";
import { RagMoreDiagramsCarousel } from "./RagMoreDiagramsCarousel";

interface RagAssistantMessageCardProps {
  msg: RagAssistantMessage;
  userQuery?: string;
  selectedOption: string | null;
  loading: boolean;
  onOptionSelect: (msgId: string, option: string) => void;
  onRelatedSelect: (question: string) => void;
  onAlternativePdf: (pdf: string) => void;
}

export function RagAssistantMessageCard({
  msg,
  userQuery,
  selectedOption,
  loading,
  onOptionSelect,
  onRelatedSelect,
  onAlternativePdf,
}: RagAssistantMessageCardProps) {
  const { t } = useTranslation();
  const badge = msg.queryType ? badgeForQueryType(msg.queryType) : null;
  const isAnswer = msg.responseType === "answer";

  const { answerBody, displayAlerts, trailingCallout } = useMemo(() => {
    if (!isAnswer) {
      return {
        answerBody: msg.answer,
        displayAlerts: msg.pageAlerts ?? {},
        trailingCallout: "",
      };
    }
    const split = splitAnswerContent(msg.answer);
    const videoOwned = isVideoOwnedProcedureAnswer(
      msg.procedure,
      msg.primarySource?.kind
    );
    // Video-owned answers keep Warnings and Notes in the body so they render as
    // a compact procedure section (often citing a companion clip). PDF answers
    // still lift that block into the Safety notices rail. Mixed pageAlerts that
    // embed Tools/Steps are sanitized in both cases.
    const displayAlerts = videoOwned
      ? mergePageAlerts(
          sanitizePageAlertsForSafetyRail(msg.pageAlerts ?? {}, msg.answer),
          extractSafetyPrecautionsAlerts(split.body)
        )
      : buildDisplayPageAlerts(
          msg.answer,
          msg.pageAlerts ?? {},
          extractSafetyPrecautionsAlerts
        );
    let answerBody = split.body;
    if (videoOwned) {
      answerBody = String(msg.answer ?? "").trim();
      if (split.trailingCallout) {
        const hr = answerBody.lastIndexOf("\n---\n");
        if (hr >= 0) answerBody = answerBody.slice(0, hr).trim();
      }
    }
    return {
      answerBody,
      displayAlerts,
      trailingCallout: split.trailingCallout,
    };
  }, [isAnswer, msg.answer, msg.pageAlerts, msg.procedure, msg.primarySource?.kind]);

  const hasSafetyAlerts =
    isAnswer &&
    Object.values(displayAlerts).some((alerts) => (alerts?.length ?? 0) > 0);

  const hasAltSources = isAnswer && (msg.alternativeSources?.length ?? 0) > 0;
  const showBestAnswerHeader = hasAltSources && !!msg.primarySource;

  const alternativePdfsToShow = useMemo(
    () => alternativePdfsNotInSources(msg.alternativePdfs, msg.alternativeSources),
    [msg.alternativePdfs, msg.alternativeSources]
  );
  const showAlternativePdfs = isAnswer && alternativePdfsToShow.length > 0;

  const videosForGlobalList = useMemo(
    () =>
      isAnswer ? getUnreferencedVideos(answerBody, msg.videos, msg.procedure) : [],
    [isAnswer, answerBody, msg.videos, msg.procedure]
  );

  const partsCatalogActive = useMemo(
    () =>
      isAnswer
        ? evaluatePartsCatalogPresentation(userQuery, answerBody, msg.inlineImages ?? {}).show
        : false,
    [isAnswer, userQuery, answerBody, msg.inlineImages]
  );

  const isProcedure = useMemo(
    () => (isAnswer ? isProcedureAnswer(answerBody) : false),
    [isAnswer, answerBody]
  );

  const diagramSelection = useMemo(
    () =>
      isAnswer && (!isProcedure || partsCatalogActive)
        ? selectMainAndMoreDiagramImages(
            msg.images ?? [],
            msg.inlineImages ?? {},
            answerBody
          )
        : { mainDiagram: null, moreDiagrams: [] as RagImageItem[] },
    [isAnswer, isProcedure, partsCatalogActive, msg.images, msg.inlineImages, answerBody]
  );

  const pageLabelShort = t("opva.chatAssistancePageShort");
  const showSpecMoreDiagrams =
    isAnswer && !isProcedure && !partsCatalogActive && diagramSelection.moreDiagrams.length > 0;

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
      <Box
        className="rag-assistant-bubble"
        sx={{
          width: "100%",
          maxWidth: "100%",
          borderRadius: "16px 16px 16px 4px",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "grey.50",
          p: 2,
          boxShadow: "0 2px 8px rgba(28,46,54,0.06)",
        }}
      >
        {badge && (
          <Chip
            size="small"
            label={badge.label}
            color={badge.chipColor}
            sx={{ mb: 1.5, fontWeight: 600 }}
          />
        )}

        {isAnswer && (
          <RagAlertsSection
            pageAlerts={displayAlerts}
            placement="top"
            sourceDocumentName={msg.primarySource?.pdf_name}
            pageLabelShort={pageLabelShort}
          />
        )}

        {showBestAnswerHeader && msg.primarySource && (
          <RagBestAnswerHeader primary={msg.primarySource} />
        )}

        <Box sx={{ mt: hasSafetyAlerts ? 1.5 : 0 }}>
          <RagAnswerText
            text={answerBody}
            inlineImages={isAnswer ? msg.inlineImages ?? {} : {}}
            showRichMedia={isAnswer}
            videos={isAnswer ? msg.videos : []}
            procedure={isAnswer ? msg.procedure : null}
            userQuery={userQuery}
            pageAlerts={isAnswer ? displayAlerts : undefined}
            specMainDiagram={
              isAnswer && !isProcedure && !partsCatalogActive
                ? diagramSelection.mainDiagram
                : null
            }
            partsMainDiagram={partsCatalogActive ? diagramSelection.mainDiagram : null}
            partsMoreDiagrams={partsCatalogActive ? diagramSelection.moreDiagrams : []}
            msgId={msg.id}
          />
          {showSpecMoreDiagrams && (
            <RagMoreDiagramsCarousel
              images={diagramSelection.moreDiagrams}
              msgId={msg.id}
              pageLabel={pageLabelShort}
              bordered
            />
          )}
          {isAnswer && isProcedure && !partsCatalogActive && (
            <RagImageRail
              images={msg.images ?? []}
              inlineImages={msg.inlineImages ?? {}}
              msgId={msg.id}
              answerText={answerBody}
            />
          )}
        </Box>

        {isAnswer && trailingCallout && (
          <Box sx={{ mt: 2 }}>
            <RagAnswerText text={trailingCallout} inlineImages={{}} showRichMedia={false} />
          </Box>
        )}

        {msg.options?.length > 0 && (
          <RagOptionButtons
            options={msg.options}
            selectedOption={selectedOption}
            disabled={loading}
            onSelect={(opt) => onOptionSelect(msg.id, opt)}
          />
        )}

        {isAnswer && <RagVideosList videos={videosForGlobalList} msgId={msg.id} />}
        {isAnswer && <RagTablesSection pageTables={msg.pageTables} defaultOpen={false} />}
        {showAlternativePdfs && (
          <RagAlternativePdfs
            pdfs={alternativePdfsToShow}
            onSelect={onAlternativePdf}
            defaultOpen={false}
          />
        )}
        {hasAltSources && (
          <RagAlternativeSources
            sources={msg.alternativeSources}
            onShowDetails={onAlternativePdf}
            defaultOpen
          />
        )}

        <RagSourcesList sources={msg.sources} msgId={msg.id} defaultOpen={!partsCatalogActive} />
        {isAnswer && (
          <RagRelatedQuestions
            questions={msg.relatedQuestions}
            onSelect={onRelatedSelect}
            maxVisible={2}
          />
        )}
      </Box>
    </Box>
  );
}
